import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import {
  Membership,
  cancelMembership,
  getCancellationQuote,
  requestCancellation,
  CancellationQuote,
} from '@/lib/portal-api-client';

interface CancelMembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  membership: Membership;
  onComplete: () => void;
  quoteRefreshNonce?: number;
}

function formatMoney(cents: number) {
  const amount = Number(cents || 0) / 100;
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function CancelMembershipModal({
  isOpen,
  onClose,
  membershipId,
  membership,
  onComplete,
  quoteRefreshNonce = 0,
}: CancelMembershipModalProps) {
  const [loading, setLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [reason, setReason] = useState('');
  const [requestedAction, setRequestedAction] = useState<'cancel_at_end' | 'payoff'>('cancel_at_end');

  const [quote, setQuote] = useState<CancellationQuote | null>(null);

  const initializedRef = useRef(false);

  async function refreshQuote() {
    setQuoteLoading(true);
    setError('');

    try {
      const res = await getCancellationQuote(membershipId);
      setQuote(res.quote);
    } catch (err: any) {
      setError(err?.message || 'Failed to load cancellation quote. Please try again.');
    } finally {
      setQuoteLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }

    // First open: reset form state.
    if (!initializedRef.current) {
      initializedRef.current = true;
      setSuccess(false);
      setError('');
      setReason('');
      setRequestedAction('cancel_at_end');
      setQuote(null);
    }

    // Always refresh quote on open and whenever the refresh nonce changes.
    void refreshQuote();
  }, [isOpen, membershipId, quoteRefreshNonce]);

  async function handleSubmit() {
    setLoading(true);
    setError('');

    try {
      if (membership.status !== 'active') {
        throw new Error('This membership is not active.');
      }

      if (!quote) {
        throw new Error('Cancellation quote is not loaded yet. Please try again.');
      }

      if (quote.can_cancel_now) {
        await cancelMembership(membershipId, reason.trim() || undefined);
      } else {
        await requestCancellation(membershipId, {
          reason: reason.trim() || undefined,
          requested_action: requestedAction,
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  const canCancelNow = Boolean(quote?.can_cancel_now);
  const hasCommitment = Boolean(quote?.commitment_payments);
  const headerTitle = quote ? (canCancelNow ? 'Cancel Membership' : 'Request Cancellation') : 'Cancel Membership';
  const headerSubtitle = quoteLoading
    ? 'Loading cancellation details...'
    : quote
      ? canCancelNow
        ? 'Stops future recurring payments.'
        : 'Submit a request — we’ll confirm payoff and next steps.'
      : 'Review payoff and submit a request.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{headerTitle}</h2>
              <p className="text-sm text-gray-600">{headerSubtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Success */}
        {success && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">All Set</h3>
            <p className="text-gray-600">
              {canCancelNow
                ? 'Your membership has been cancelled.'
                : 'Your cancellation request has been submitted. Our team will follow up shortly.'}
            </p>
          </div>
        )}

        {!success && (
          <div className="p-6 space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-800 font-semibold">Important</p>
              <p className="text-sm text-red-700 mt-1">
                {canCancelNow
                  ? 'Cancelling will stop future recurring payments for this membership.'
                  : hasCommitment && quote
                    ? `Monthly memberships are a ${quote.commitment_payments}-payment commitment. You can request cancellation at the end of the commitment, or request an early payoff to cancel sooner. Submitting a request does not automatically stop billing today—we’ll confirm any payoff and the effective cancellation date.`
                    : 'Submitting a cancellation request does not automatically stop billing today—our team will follow up with next steps.'}
              </p>
            </div>

            {/* Commitment/Payoff summary */}
            {quoteLoading ? (
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3 text-sm text-gray-700">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading payoff details...
              </div>
            ) : hasCommitment && quote ? (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800">Commitment summary</p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void refreshQuote()}
                      disabled={quoteLoading}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
                    >
                      Refresh payoff
                    </button>
                    <p className="text-xs text-gray-500">Monthly plan</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                  <div>
                    <p className="text-gray-600">Payments made</p>
                    <p className="font-semibold text-gray-900">
                      {quote.payments_made} of {quote.commitment_payments}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Payments remaining</p>
                    <p className="font-semibold text-gray-900">{quote.remaining_payments}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-600">Estimated payoff amount</p>
                    <p className="font-semibold text-gray-900">{formatMoney(quote.payoff_amount_cents)}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Estimates are based on successful payments recorded in your portal. Final payoff will be confirmed by our
                  team.
                </p>
              </div>
            ) : null}

            {/* Action selection (only if payoff is needed) */}
            {!canCancelNow && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-800">What would you like to do?</p>

                <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  requestedAction === 'cancel_at_end' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="requested_action"
                    value="cancel_at_end"
                    checked={requestedAction === 'cancel_at_end'}
                    onChange={() => setRequestedAction('cancel_at_end')}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Cancel at end of commitment</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Your membership stays active and billing continues as scheduled until the commitment is complete.
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  requestedAction === 'payoff' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="requested_action"
                    value="payoff"
                    checked={requestedAction === 'payoff'}
                    onChange={() => setRequestedAction('payoff')}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">Cancel early (payoff)</p>
                    <p className="text-sm text-gray-600 mt-1">
                      We’ll confirm the payoff amount and contact you to complete payment and finalize the cancellation.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-800">What happens next</p>
              {canCancelNow ? (
                <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc pl-5">
                  <li>We’ll cancel your membership and stop future recurring payments.</li>
                  <li>You’ll receive an email confirmation once everything is processed.</li>
                </ul>
              ) : (
                <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc pl-5">
                  <li>Submitting a request does not automatically stop billing today.</li>
                  <li>We’ll review your request and confirm any remaining balance (if applicable).</li>
                  <li>If you chose payoff, we’ll reach out to collect payment and confirm the effective cancellation date.</li>
                  <li>You’ll receive an email confirmation once everything is processed.</li>
                </ul>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Tell us what’s driving the cancellation..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                Keep Membership
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || quoteLoading || !quote}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-orange-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5" />
                    {canCancelNow ? 'Cancel Membership' : 'Submit Cancellation Request'}
                  </>
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Need help? Contact customer support for assistance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
